/**
 * Unified Google OAuth callback SSOT — redirect URI + returnOrigin allowlist
 * @see Stream P1: single Google Console URI /auth/oauth/callback/google
 */

import { isDevEnv } from '@shared/env/isDevEnv';
import {
  isCustomDomainMailActive,
  normalizeMailDomain,
  tenantMailSettingsSchema,
} from '@shared/types/tenant-mail-settings';
import { getTenantIdFromHost, normalizeHostname } from '@shared/tenant-host/getTenantIdFromHost';
import { resolveTenantRootDomain } from '@shared/tenant-host/defaultRootDomain';
import type { TenantOAuthCallbackProvider } from './tenant-oauth-provider-registry';

export interface IsAllowedReturnOriginOptions {
  /** Verified enterprise custom domains (TM-5 active + verifiedAt). */
  verifiedCustomDomains?: readonly string[];
}

/** Canonical unified callback base (platform host). */
export const UNIFIED_OAUTH_CALLBACK_PATH = '/auth/oauth/callback' as const;

/** @deprecated use UNIFIED_OAUTH_CALLBACK_PATH — alias for tenant central callback consumers */
export const TENANT_OAUTH_CENTRAL_CALLBACK_PATH = UNIFIED_OAUTH_CALLBACK_PATH;

function resolveFrontendBase(): string {
  return (process.env.FRONTEND_URL?.trim() || 'http://localhost:5173').replace(/\/$/, '');
}

/** Platform host base for unified OAuth callbacks (FRONTEND_URL + /auth/oauth/callback). */
export function resolveUnifiedOAuthCallbackBase(): string {
  return `${resolveFrontendBase()}${UNIFIED_OAUTH_CALLBACK_PATH}`;
}

/** @deprecated use resolveUnifiedOAuthCallbackBase */
export function resolveTenantOAuthCentralCallbackBase(): string {
  return resolveUnifiedOAuthCallbackBase();
}

/** @deprecated use resolveUnifiedOAuthCallbackBase */
export const TENANT_OAUTH_CENTRAL_CALLBACK_BASE = resolveUnifiedOAuthCallbackBase();

/** Fixed redirect URI registered once in Google Console for platform_managed + platform flows. */
export function buildCentralCallbackUri(provider: TenantOAuthCallbackProvider): string {
  return `${resolveUnifiedOAuthCallbackBase()}/${provider}`;
}

/**
 * Platform OAuth origins accepted for central callback URIs.
 * Vercel production canonicalizes apex → www; allow both when they share TENANT_ROOT_DOMAIN.
 */
export function resolvePlatformOAuthCallbackOrigins(): string[] {
  const frontendBase = resolveFrontendBase();
  let parsed: URL;
  try {
    parsed = new URL(frontendBase);
  } catch {
    return [frontendBase];
  }

  const origins = new Set<string>([parsed.origin]);
  const host = normalizeHostname(parsed.hostname);
  const rootDomain = resolveTenantRootDomain(process.env.TENANT_ROOT_DOMAIN);

  if (host === rootDomain) {
    origins.add(`${parsed.protocol}//www.${rootDomain}`);
  } else if (host === `www.${rootDomain}`) {
    origins.add(`${parsed.protocol}//${rootDomain}`);
  }

  return [...origins];
}

function buildCentralCallbackUriForOrigin(
  origin: string,
  provider: TenantOAuthCallbackProvider
): string {
  return `${origin.replace(/\/$/, '')}${UNIFIED_OAUTH_CALLBACK_PATH}/${provider}`;
}

/** All central callback URIs for configured platform origins (SSOT + www/apex alias). */
export function enumerateCentralCallbackUris(provider?: TenantOAuthCallbackProvider): string[] {
  const providers: TenantOAuthCallbackProvider[] = provider
    ? [provider]
    : (['google', 'apple', 'kakao'] as TenantOAuthCallbackProvider[]);

  const uris = new Set<string>();
  for (const origin of resolvePlatformOAuthCallbackOrigins()) {
    for (const p of providers) {
      uris.add(buildCentralCallbackUriForOrigin(origin, p));
    }
  }
  return [...uris];
}

/** Browser helper — compose unified Google callback from current platform origin. */
export function buildUnifiedGoogleOAuthCallbackUri(origin: string): string {
  return `${origin.replace(/\/$/, '')}${UNIFIED_OAUTH_CALLBACK_PATH}/google`;
}

export function isAllowedCentralRedirectUri(
  uri: string,
  provider?: TenantOAuthCallbackProvider
): boolean {
  const trimmed = uri.trim().replace(/\/$/, '');
  if (!trimmed) return false;

  return enumerateCentralCallbackUris(provider).includes(trimmed);
}

/** Extract verified custom domain hostnames from tenants.settings (mail.customDomain). */
export function resolveVerifiedCustomDomainHostsFromSettings(
  settings: Record<string, unknown> | null | undefined
): string[] {
  const parsed = tenantMailSettingsSchema.safeParse(settings?.mail);
  if (!parsed.success) {
    return [];
  }

  const customDomain = parsed.data.customDomain;
  if (!isCustomDomainMailActive(customDomain)) {
    return [];
  }

  return [normalizeMailDomain(customDomain.domain)];
}

function matchesVerifiedCustomDomain(hostname: string, domains: readonly string[]): boolean {
  const host = normalizeMailDomain(normalizeHostname(hostname));
  return domains.some((domain) => normalizeMailDomain(domain) === host);
}

/**
 * Validates returnOrigin against tenantId (subdomain, verified custom domain, or dev localhost).
 * Blocks open redirects to arbitrary origins.
 */
export function isAllowedReturnOrigin(
  origin: string,
  tenantId: string,
  options?: IsAllowedReturnOriginOptions
): boolean {
  const slug = tenantId.trim();
  if (!slug) return false;

  let parsed: URL;
  try {
    parsed = new URL(origin.trim());
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return false;
  }

  if (parsed.pathname !== '/' || parsed.search || parsed.hash) {
    return false;
  }

  const hostname = normalizeHostname(parsed.hostname);
  const rootDomain = resolveTenantRootDomain(process.env.TENANT_ROOT_DOMAIN);
  const fromHost = getTenantIdFromHost(hostname, rootDomain);
  if (fromHost === slug) {
    return true;
  }

  if (hostname === `${slug}.localhost`) {
    return true;
  }

  if (isDevEnv() && (hostname === 'localhost' || hostname === '127.0.0.1')) {
    return true;
  }

  const verifiedCustomDomains = options?.verifiedCustomDomains;
  if (
    verifiedCustomDomains?.length &&
    matchesVerifiedCustomDomain(hostname, verifiedCustomDomains)
  ) {
    return true;
  }

  return false;
}

/** Default tenant origin for handoff redirect when caller omits returnOrigin. */
export function buildDefaultReturnOrigin(tenantId: string): string {
  const slug = tenantId.trim();
  const rootDomain = resolveTenantRootDomain(process.env.TENANT_ROOT_DOMAIN);

  if (isDevEnv()) {
    const frontend = resolveFrontendBase();
    try {
      const base = new URL(frontend);
      const port = base.port ? `:${base.port}` : '';
      return `${base.protocol}//${slug}.localhost${port}`;
    } catch {
      return `http://${slug}.localhost:5173`;
    }
  }

  return `https://${slug}.${rootDomain}`;
}
